<template>
	<v-calendar class="route-optimizer-calendar" :model-value="start" :type="type" :start="start" :events="events" :weekdays="weekdays" :first-interval="intervals.first" :interval-count="intervals.count" :interval-minutes="60" :interval-height="64" :interval-format="intervalFormat" :locale="locale" event-overlap-mode="column" event-color="transparent" :event-ripple="false">
		<template #day-header="{ date }">
			<div v-if="type === 'week' && dayGain[date]" class="route-optimizer-calendar__day-gain">{{ dayGain[date] }}</div>
		</template>
		<template #event="{ event }">
			<div v-if="event.kind === 'stop'" class="route-optimizer-calendar__event" :style="stopStyle(event.step)" :title="event.step.item.label">
				<div class="route-optimizer-calendar__event-head">
					<span class="route-optimizer-calendar__event-time">
						{{ formatTime(event.start) }} · {{ formatTime(event.end) }}
						<s v-if="mode === 'after' && event.step.moved !== MOVE.UNCHANGED" class="route-optimizer-calendar__event-was" :title="`${t('WasAt')} ${formatTime(event.step.oldStart)}`">{{ formatTime(event.step.oldStart) }}</s>
					</span>
					<span v-if="badgeOf(event.step)" class="route-optimizer-calendar__badge" :class="`route-optimizer-calendar__badge--${badgeOf(event.step).tone}`">
						<v-icon size="12">{{ badgeOf(event.step).icon }}</v-icon>
						<span v-if="type === 'day'">{{ t(badgeOf(event.step).key) }}</span>
					</span>
				</div>
				<div class="route-optimizer-calendar__event-title">{{ event.step.item.label }}</div>
				<div v-if="event.step.item.address && type === 'day'" class="route-optimizer-calendar__event-sub">{{ event.step.item.address }}</div>
			</div>
			<div v-else class="route-optimizer-calendar__leg" :title="legLabel(event)">
				<v-icon size="12">{{ event.kind === 'departure' ? departureIcon(event) : 'directions_car' }}</v-icon>
				<span>{{ legLabel(event) }}</span>
			</div>
		</template>
	</v-calendar>
</template>

<script setup>
import { computed } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { buildTimelineEvents, formatGain, formatMinutes, MOVE, STEP_KIND } from '@/utils/domain/routeOptimizer';
import { toDayKey } from '@/utils/legacy/agendaRouteSource';

const props = defineProps({
	days: { type: Array, default: () => [] },
	mode: { type: String, default: 'after' },
	type: { type: String, default: 'day' },
	start: { type: String, required: true },
	weekdays: { type: Array, default: () => [1, 2, 3, 4, 5] },
});

const t = useTranslator();
const user = iframeDataBridge.getCurrentUser();
const locale = user?.locale || 'fr-FR';
const pad = (n) => String(n).padStart(2, '0');

const formatTime = (ms) => {
	const d = new Date(ms);
	return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const intervalFormat = (timestamp) => `${pad(timestamp.hour)} h`;

const events = computed(() =>
	props.days.flatMap((day) =>
		buildTimelineEvents(day, props.mode).map((e) => ({
			...e,
			id: `${day.date}-${e.id}`,
			date: day.date,
			name: e.step?.item.label || '',
			timed: true,
		}))
	)
);

const intervals = computed(() => {
	if (!events.value.length) return { first: 8, count: 10 };
	const firstHour = Math.min(...events.value.map((e) => new Date(e.start).getHours()));
	const lastHour = Math.max(
		...events.value.map((e) => {
			const end = new Date(e.end);
			return toDayKey(end) !== toDayKey(e.start) ? 24 : end.getHours() + (end.getMinutes() ? 1 : 0);
		})
	);
	return { first: firstHour, count: Math.max(lastHour - firstHour, 4) };
});

const dayGain = computed(() => Object.fromEntries(props.days.filter((d) => d.travelBefore !== d.travelAfter).map((d) => [d.date, formatGain(d.travelBefore - d.travelAfter)])));

function badgeOf(step) {
	if (props.mode !== 'after') return null;
	if (step.kind === STEP_KIND.PINNED) return { icon: 'keep', key: 'PinnedSlotKept', tone: 'pinned' };
	if (step.kind === STEP_KIND.UNPLACEABLE) return { icon: 'schedule', key: 'Unplaceable', tone: 'unplaceable' };
	if (step.kind !== STEP_KIND.VISIT) return { icon: 'keep_off', key: 'NotMoved', tone: 'neutral' };
	if (step.moved === MOVE.EARLIER) return { icon: 'arrow_upward', key: 'Earlier', tone: 'earlier' };
	if (step.moved === MOVE.LATER) return { icon: 'arrow_downward', key: 'Later', tone: 'later' };
	return { icon: 'check', key: 'PositionUnchanged', tone: 'neutral' };
}

const stopStyle = (step) => ({ ...(step.item.color && { '--route-accent': step.item.color }), ...(step.item.bgColor && { background: step.item.bgColor }) });

const departureIcon = (event) => (props.days.find((d) => d.date === event.date)?.startKind === 'night' ? 'hotel' : 'home');

function legLabel(event) {
	if (event.kind === 'departure') {
		const startKind = props.days.find((d) => d.date === event.date)?.startKind;
		return `${t(startKind === 'night' ? 'RouteDepartureHotel' : 'RouteDepartureHome')} · ${formatTime(event.start)} · ${formatMinutes(event.minutes)}`;
	}
	return `${t('Route')} · ${formatMinutes(event.minutes)}`;
}
</script>
